import { action } from 'mobx';
import { SessionStore } from './session.store';
import { AggregateFunction, AgtAnalyticsStore, SelectColumn } from './agt.analytics.store';

export interface IAkumaPlanerModel {
	vtnrId: number;
	kw: number;
	zagt: number;
	nb: number;
	zma: number;
	km: number;
	nu: number;
	zu: number;
	gesamt: number;
	gpWochen: number;
	// Legacy compatibility properties
	bm: number;
	year: number;
	agtId: number;
	gpNoWochen: number;
}

export class AkumaPlanerModel implements IAkumaPlanerModel {
	vtnrId: number;
	kw: number;
	zagt: number;
	nb: number;
	zma: number;
	km: number;
	nu: number;
	zu: number;
	gesamt: number;
	wochen: number;
	gpWochen: number;
	// Legacy compatibility properties
	bm: number;
	year: number;
	agtId: number;
	gpNoWochen: number;
	maxkw: number;

	constructor(data: any) {
		this.vtnrId = data.vtnr_id || 0;
		this.kw = data.kw || 0;
		this.zagt = data.zagt || 0;
		this.nb = data.nb || 0;
		this.zma = data.zma || 0;
		this.km = data.km || 0;
		this.nu = data.nu || 0;

		this.zu = data.zu || 0;
		this.wochen = data.wochen || 0;
		this.gesamt = data.gesamt || 0;
		this.gpWochen = data.gpwochen || 0;
		this.gpNoWochen = data.gpnowochen || 0;
		this.maxkw = data.maxkw || 0;

		// Legacy compatibility - alias properties
		this.bm = this.kw;
		this.year = Math.floor(this.kw / 100);
		this.agtId = this.vtnrId;
	}
}

export class AkumaPlanerWeeklyModel {
	kw: number;
	quote: number;
	gpWochen: number;
	zugewiesen: number;
	zuweisbar: number;

	constructor(data: any) {
		this.kw = data.kw || 0;
		this.quote = data.quote || 0;
		this.gpWochen = data.gpwochen || 0;
		this.zugewiesen = data.zugewiesen || 0;
		this.zuweisbar = data.zuweisbar || 0;
	}
}

export class AgtAkumaPlanerStore extends AgtAnalyticsStore {
	constructor(session: SessionStore) {
		super(session, {
			tableName: 'agentur_akumaplaner',
			defaultColumns: [
				{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE },
				{ column: 'kw', aggregateFunction: AggregateFunction.NONE },
				{ column: 'zagt', aggregateFunction: AggregateFunction.SUM },
				{ column: 'nb', aggregateFunction: AggregateFunction.SUM },
				{ column: 'zma', aggregateFunction: AggregateFunction.SUM },
				{ column: 'km', aggregateFunction: AggregateFunction.SUM },
				{ column: 'nu', aggregateFunction: AggregateFunction.SUM },
				{ column: 'zugewiesen', aggregateFunction: AggregateFunction.SUM, alias: 'zu' },
				{ column: 'zuweisbar', aggregateFunction: AggregateFunction.SUM, alias: 'gesamt' },

				{ column: 'gpWochen', aggregateFunction: AggregateFunction.SUM, alias: 'gpWochen' },
				{ column: 'gpNoWochen', aggregateFunction: AggregateFunction.SUM, alias: 'gpNoWochen' },
				// { column: 'quote', aggregateFunction: AggregateFunction.SUM, alias: 'quote' },
			],
		});
		this.queryName = '?akumaPlaner';
	}

	async getDistinctKWs(): Promise<number[]> {
		const selectColumns: SelectColumn[] = [{ column: 'kw', aggregateFunction: AggregateFunction.NONE, distinct: true }];
		const results = await this.query(selectColumns);
		return results.map((data) => data.kw).sort((a, b) => a - b);
	}

	@action
	async aggregateByAgt(minKw?: number, maxKw?: number, ssVmFilter?: string[]): Promise<AkumaPlanerModel[]> {
		let whereClause = '';
		if (minKw && maxKw) {
			whereClause = `kw BETWEEN ${minKw} AND ${maxKw}`;
		} else if (minKw) {
			whereClause = `kw >= ${minKw}`;
		} else if (maxKw) {
			whereClause = `kw <= ${maxKw}`;
		}

		// Add agency filters if provided
		if (ssVmFilter && ssVmFilter.length > 0) {
			if (whereClause) {
				// If we already have a WHERE clause, use AND
				const filterValues = ssVmFilter.map((v) => `AND ${v}`);
				whereClause = whereClause + ' ' + filterValues.join(' ');
			} else {
				// If whereClause is empty, don't use AND for the first filter
				whereClause = ssVmFilter.join(' AND ');
			}
		}

		const selectColumns: SelectColumn[] = [
			{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE, groupBy: true },
			{ column: 'zagt', aggregateFunction: AggregateFunction.SUM, alias: 'zagt' },
			{ column: 'nb', aggregateFunction: AggregateFunction.SUM, alias: 'nb' },
			{ column: 'zma', aggregateFunction: AggregateFunction.SUM, alias: 'zma' },
			{ column: 'km', aggregateFunction: AggregateFunction.SUM, alias: 'km' },
			{ column: 'nu', aggregateFunction: AggregateFunction.SUM, alias: 'nu' },
			{ column: 'zugewiesen', aggregateFunction: AggregateFunction.SUM, alias: 'zu' },
			{ column: 'zuweisbar', aggregateFunction: AggregateFunction.SUM, alias: 'gesamt' },
			{ column: 'gpWochen', aggregateFunction: AggregateFunction.SUM, alias: 'gpWochen' },
			{ column: 'gpNoWochen', aggregateFunction: AggregateFunction.SUM, alias: 'gpNoWochen' },
			{ column: 'kw', aggregateFunction: AggregateFunction.MAX, alias: 'maxkw' },
			{ column: 'kw', aggregateFunction: AggregateFunction.COUNT_DISTINCT, alias: 'wochen' },
		];

		const results = await this.query(selectColumns, whereClause);
		const models = results.map((data) => new AkumaPlanerModel(data));
		return models;
	}

	@action
	async loadWeeklyByAgt(agtId: number): Promise<AkumaPlanerWeeklyModel[]> {
		const whereClause = `vtnr_id = ${agtId} AND kw BETWEEN 202601 AND 202653`;
		const selectColumns: SelectColumn[] = [
			{ column: 'kw', aggregateFunction: AggregateFunction.NONE },
			{ column: 'quote', aggregateFunction: AggregateFunction.NONE },
			{ column: 'gpWochen', aggregateFunction: AggregateFunction.NONE },
			{ column: 'zugewiesen', aggregateFunction: AggregateFunction.NONE },
			{ column: 'zuweisbar', aggregateFunction: AggregateFunction.NONE },
		];
		const results = await this.query(selectColumns, whereClause);
		return results.map((data) => new AkumaPlanerWeeklyModel(data)).sort((a, b) => a.kw - b.kw);
	}

	@action
	async aggregateByKw(minKw?: number, maxKw?: number, ssVmFilter?: string[]): Promise<AkumaPlanerModel[]> {
		let whereClause;
		if (minKw && maxKw) {
			whereClause = `kw BETWEEN ${minKw} AND ${maxKw}`;
		} else if (minKw) {
			whereClause = `kw >= ${minKw}`;
		} else if (maxKw) {
			whereClause = `kw <= ${maxKw}`;
		}

		// Add agency filters if provided
		if (ssVmFilter && ssVmFilter.length > 0) {
			if (whereClause) {
				// If we already have a WHERE clause, use AND
				const filterValues = ssVmFilter.map((v) => `AND ${v}`);
				whereClause = whereClause + ' ' + filterValues.join(' ');
			} else {
				// If whereClause is empty, don't use AND for the first filter
				whereClause = ssVmFilter.join(' AND ');
			}
		}

		const selectColumns: SelectColumn[] = [
			{ column: 'kw', aggregateFunction: AggregateFunction.NONE, groupBy: true },
			{ column: 'zagt', aggregateFunction: AggregateFunction.SUM, alias: 'zagt' },
			{ column: 'nb', aggregateFunction: AggregateFunction.SUM, alias: 'nb' },
			{ column: 'zma', aggregateFunction: AggregateFunction.SUM, alias: 'zma' },
			{ column: 'km', aggregateFunction: AggregateFunction.SUM, alias: 'km' },
			{ column: 'nu', aggregateFunction: AggregateFunction.SUM, alias: 'nu' },
			{ column: 'zugewiesen', aggregateFunction: AggregateFunction.SUM, alias: 'zu' },
			{ column: 'zuweisbar', aggregateFunction: AggregateFunction.SUM, alias: 'gesamt' },
		];

		const results = await this.query(selectColumns, whereClause);
		const models = results.map((data) => new AkumaPlanerModel(data));
		// Sort by bm (kw) from highest to lowest
		return models.sort((a, b) => b.bm - a.bm);
	}
}
